<script setup>
/* global axios */
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AModal from './AModal.vue';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const base = `/api/v1/accounts/${props.accountId}/kanban`;

const patients = ref([]);
const healthPlans = ref([]);
const search = ref('');
const show = ref(false);
const form = ref({});

const load = async () => {
  patients.value =
    (await axios.get(`${base}/patients`, { params: { q: search.value } })).data
      .data || [];
};
const open = (p = null) => {
  form.value = p
    ? { ...p }
    : {
        name: '',
        phone: '',
        email: '',
        cpf: '',
        health_plan_id: '',
        notes: '',
      };
  show.value = true;
};
const save = async () => {
  if (!form.value.name?.trim()) return;
  if (form.value.id)
    await axios.patch(`${base}/patients/${form.value.id}`, form.value);
  else await axios.post(`${base}/patients`, form.value);
  show.value = false;
  await load();
};
const remove = async p => {
  if (
    !window.confirm(
      t('ATENDIMENTOS.patients.deleteConfirmShort', { name: p.name })
    )
  )
    return;
  await axios.delete(`${base}/patients/${p.id}`);
  await load();
};
const planName = id =>
  healthPlans.value.find(h => h.id === id)?.name ||
  t('ATENDIMENTOS.partners.types.particular');

onMounted(async () => {
  await load();
  healthPlans.value = (await axios.get(`${base}/health_plans`)).data.data || [];
});
</script>

<template>
  <div class="p-6">
    <div class="flex gap-3 mb-4">
      <input
        v-model="search"
        class="flex-1 w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        :placeholder="t('ATENDIMENTOS.patients.search')"
        @input="load"
      />
      <button
        class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
        @click="open()"
      >
        {{ t('ATENDIMENTOS.patients.newClient') }}
      </button>
    </div>
    <div class="overflow-hidden bg-n-solid-2 border rounded-xl border-n-weak">
      <table class="min-w-full text-sm divide-y divide-n-weak">
        <thead class="bg-n-alpha-1">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              {{ t('ATENDIMENTOS.patients.columns.name') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              {{ t('ATENDIMENTOS.patients.columns.phone') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              {{ t('ATENDIMENTOS.patients.editModal.emailfield') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              {{ t('ATENDIMENTOS.patients.columns.partner') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10 text-right"
            >
              {{ t('ATENDIMENTOS.patients.columns.actions') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr v-for="p in patients" :key="p.id" class="hover:bg-n-alpha-1">
            <td class="px-4 py-3 text-n-slate-12">{{ p.name }}</td>
            <td class="px-4 py-3 text-n-slate-11">{{ p.phone }}</td>
            <td class="px-4 py-3 text-n-slate-11">{{ p.email || '—' }}</td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ planName(p.health_plan_id) }}
            </td>
            <td class="px-4 py-3 text-right">
              <button
                class="text-xs text-n-blue-11 hover:underline"
                @click="open(p)"
              >
                {{ t('ATENDIMENTOS.patients.edit') }}
              </button>
              <button
                class="ml-2 text-xs text-n-ruby-11 hover:underline"
                @click="remove(p)"
              >
                {{ t('ATENDIMENTOS.patients.delete') }}
              </button>
            </td>
          </tr>
          <tr v-if="!patients.length">
            <td colspan="5" class="px-4 py-8 text-center text-n-slate-10">
              {{ t('ATENDIMENTOS.patients.noResults') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <AModal
      v-if="show"
      :title="
        form.id
          ? t('ATENDIMENTOS.patients.editModal.editTitle')
          : t('ATENDIMENTOS.patients.editModal.newTitle')
      "
      size="sm"
      @close="show = false"
    >
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.patients.editModal.namefield')
        }}</label
        ><input
          v-model="form.name"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.patients.editModal.phonefield')
        }}</label
        ><input
          v-model="form.phone"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.patients.editModal.emailfield')
        }}</label
        ><input
          v-model="form.email"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.patients.editModal.cpffield')
        }}</label
        ><input
          v-model="form.cpf"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.patients.editModal.partnerField')
        }}</label
        ><select
          v-model="form.health_plan_id"
          class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option value="">
            {{ t('ATENDIMENTOS.patients.editModal.nonePrivate') }}
          </option>
          <option v-for="h in healthPlans" :key="h.id" :value="h.id">
            {{ h.name }}
          </option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.patients.editModal.notesField')
        }}</label
        ><textarea
          v-model="form.notes"
          rows="2"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
      </div>
      <template #footer>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="show = false"
        >
          {{ t('ATENDIMENTOS.patients.editModal.cancel') }}</button
        ><button
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
          @click="save"
        >
          {{ t('ATENDIMENTOS.patients.editModal.save') }}
        </button>
      </template>
    </AModal>
  </div>
</template>
